import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Download, Trophy, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";

export default function RecapView() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/rooms/${code}/recap`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [code]);

  const saveImage = async () => {
    if (!cardRef.current) return;
    setSaving(true);
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#141228", scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `hitmaster-recap-${code}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Recap opgeslagen als afbeelding ✓");
    } catch {
      toast.error("Opslaan als afbeelding mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6 text-center">
        <div className="font-bebas text-white text-3xl tracking-wide">Recap niet gevonden 😕</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy py-6 px-4 flex flex-col items-center" data-testid="recap-view">
      <div ref={cardRef} className="w-full max-w-lg bg-navy px-4 py-5" style={{ background: "#141228" }}>
        <div className="text-center mb-4">
          <div className="font-bebas text-gold text-5xl tracking-widest leading-none">HITMASTER</div>
          <div className="font-display font-bold text-cream/70 text-sm uppercase tracking-widest mt-1">Game Recap · {data.code}</div>
        </div>

        {/* Final standings */}
        <div className="rounded-2xl border-2 border-gold/60 bg-white/5 p-3 mb-4">
          <div className="font-bebas text-white text-2xl tracking-widest uppercase mb-2 flex items-center justify-center gap-2">
            <Trophy className="w-6 h-6 text-gold" strokeWidth={2.5} /> Eindstand
          </div>
          <div className="flex flex-col gap-1.5">
            {data.players.map((p, i) => (
              <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-1.5 ${i === 0 ? "bg-gold/25 ring-2 ring-gold" : "bg-white/8"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-6 h-6 rounded-full bg-cream text-navy font-display font-black flex items-center justify-center text-xs shrink-0">{i + 1}</span>
                  <span className="font-display font-bold text-white truncate">{p.name}</span>
                </div>
                <span className="font-display font-black text-gold text-lg tabular-nums">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Song-by-song recap */}
        <div className="font-bebas text-white text-2xl tracking-widest uppercase mb-2 text-center">Gespeelde nummers</div>
        <div className="flex flex-col gap-2">
          {data.history.map((h) => {
            const scorers = (h.scorers || []).filter((s) => s.points > 0);
            return (
              <div key={h.round} className="rounded-xl border-2 border-white/20 bg-white/8 px-3 py-2" data-testid={`recap-view-round-${h.round}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-display font-black text-white truncate min-w-0">
                    {h.round}. {h.title || "—"} <span className="text-cream/60 font-bold">· {h.artist || "—"}</span>
                  </div>
                  {h.year ? <span className="shrink-0 text-xs font-black bg-gold text-navy px-2 py-0.5 rounded-full">{h.year}</span> : null}
                </div>
                <div className="text-xs font-bold text-cream/60 mt-1">
                  {scorers.length > 0
                    ? scorers.map((s) => `${s.name} +${s.points}`).join(" · ")
                    : "Niemand scoorde"}
                </div>
              </div>
            );
          })}
          {data.history.length === 0 && (
            <div className="text-center text-cream/50 font-bold py-4">Geen rondes gespeeld</div>
          )}
        </div>
      </div>

      <button
        data-testid="recap-save-image-btn"
        onClick={saveImage}
        disabled={saving}
        className="mt-5 bg-gold text-navy font-display font-black uppercase px-6 py-3 rounded-xl border-2 border-navy shadow-[0_4px_0_#000] flex items-center gap-2 disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" strokeWidth={2.6} />}
        Bewaar als afbeelding
      </button>
    </div>
  );
}
